import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchReprocessingInvoiceFile } from './fetchReprocessingInvoiceFile';
import { type InvoiceReprocessingFileType } from './types';

/**
 * React Query based custom hook for getting a file referenced by an invoice requiring reprocessing based on the file ID.
 * @param params - The parameters for fetching the reprocessing invoice file.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the file content.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_file_invoice_process__process_id__file__file_id__get
 */
export const useQueryReprocessingInvoiceFile = (
  {
    fileId,
    fileType = 'application/pdf',
    processId,
  }: {
    fileId: UUID;
    fileType?: InvoiceReprocessingFileType;
    processId: UUID;
  },
  options?: Omit<
    UseQueryOptions<Blob | string | Record<string, unknown>>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<Blob | string | Record<string, unknown>>({
    enabled: Boolean(fileId) && Boolean(fileType) && Boolean(processId),
    gcTime: ms('1h'), // clear cache after 1 hour
    queryFn: async () =>
      fetchReprocessingInvoiceFile({
        fileId,
        fileType,
        processId,
      }),
    queryKey: queryKeysInvoiceReprocessing.getFile({
      fileId,
      fileType,
      processId,
    }),
    staleTime: Infinity, // only re-fetch if explicitly invalidated
    ...options,
  });
};
